import { User, LoginCredentials, RegisterData } from '@/types';

// Mock user data for demo purposes
const mockUsers: User[] = [
  {
    id: '1',
    name: 'Admin User',
    email: 'admin@city.gov',
    role: 'admin'
  },
  {
    id: '2',
    name: 'John Citizen',
    email: 'john@example.com',
    role: 'user'
  }
];

const CURRENT_USER_KEY = 'currentUser';
const SESSION_META_KEY = 'sessionMeta';

interface SessionMeta {
  loginAt: Date;
  role: User['role'];
}

export const authService = {
  /* =========================
     EXISTING METHODS (UNCHANGED)
     ========================= */

  login: async (credentials: LoginCredentials): Promise<User | null> => {
    await new Promise(resolve => setTimeout(resolve, 1000));

    const user = mockUsers.find(u => u.email === credentials.email);
    if (user && (credentials.password === 'password' || credentials.password === 'admin')) {
      localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(user));

      // ✅ ADD: session metadata
      const meta: SessionMeta = {
        loginAt: new Date(),
        role: user.role,
      };
      localStorage.setItem(SESSION_META_KEY, JSON.stringify(meta));

      return user;
    }
    return null;
  },

  register: async (data: RegisterData): Promise<User> => {
    await new Promise(resolve => setTimeout(resolve, 1000));

    const newUser: User = {
      id: Date.now().toString(),
      name: data.name,
      email: data.email,
      phone: data.phone,
      role: 'user'
    };

    localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(newUser));

    // ✅ ADD: session metadata
    const meta: SessionMeta = {
      loginAt: new Date(),
      role: newUser.role,
    };
    localStorage.setItem(SESSION_META_KEY, JSON.stringify(meta));

    return newUser;
  },

  logout: () => {
    localStorage.removeItem(CURRENT_USER_KEY);
    localStorage.removeItem(SESSION_META_KEY);
  },

  getCurrentUser: (): User | null => {
    const userStr = localStorage.getItem(CURRENT_USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  },

  /* =========================
     ✅ NEW SAFE HELPERS (ADD-ONLY)
     ========================= */

  /** 🔐 Admin role check */
  isAdmin(): boolean {
    const user = authService.getCurrentUser();
    return user?.role === 'admin';
  },

  /** 🧠 Session insight (for analytics / audit) */
  getSessionInfo(): SessionMeta | null {
    const meta = localStorage.getItem(SESSION_META_KEY);
    return meta ? { ...JSON.parse(meta), loginAt: new Date(JSON.parse(meta).loginAt) } : null;
  },

  /** 🚫 Guard utility (future API / route protection) */
  requireAdmin(): boolean {
    if (!authService.isAdmin()) {
      console.warn('Admin access denied');
      return false;
    }
    return true;
  },

  /** 🛡 Null-safe getter */
  getCurrentUserSafe(): User {
    const user = authService.getCurrentUser();
    if (!user) {
      throw new Error('No authenticated user found');
    }
    return user;
  }
};
